/**
 * Course CE Information — derived from the backend `Course.ceInfo` field.
 *
 * Continuing-education metadata an admin authors per course on the
 * course settings page. Separate from the generic "Custom Fields"
 * (which surface in catalog filters) because CE fields are part of
 * the learner-facing "CE Information" tab and need a stable shape.
 *
 * The persisted shape (`CourseCeInfo` from the API client) has every field
 * optional. This module normalizes it into a `CourseCEInfo` where every
 * field is a string (empty when unset) so consumers can render without
 * null-checking. Blank fields stay blank — nothing is substituted — so the
 * CE tab only shows what an admin actually authored. Writes go through
 * `coursesClient.updateCourse` with a `settings.ceInfo` payload — see
 * `utils/courseSettings.ts`.
 */

import { CourseCeInfo } from '../apis/api-client';

export interface CourseCEInfo {
  /** AGD subject code (e.g. "550" for OSHA / Risk Management). */
  agdSubjectCode: string;
  /** AGD PACE Provider ID — same number used on the PACE seal. */
  paceProviderId: string;
  /** Educational method (e.g. "Lecture, video, quiz"). */
  educationalMethod: string;
  /** Where the CE is delivered ("Online", a city, a venue name). */
  location: string;
  /** Accreditation statement / boilerplate — rich text HTML. */
  accreditationStatement: string;
  /** Conflict-of-interest / disclosure statement — rich text HTML. */
  disclosureStatement: string;
  /** PACE accreditation badge image URL. Empty string falls back to
   *  the bundled AGD PACE asset so every course renders the seal by
   *  default; admins can upload a custom badge per course. */
  paceBadgeUrl: string;
}

export const EMPTY_CE_INFO: CourseCEInfo = {
  agdSubjectCode: '',
  paceProviderId: '',
  educationalMethod: '',
  location: '',
  accreditationStatement: '',
  disclosureStatement: '',
  paceBadgeUrl: '',
};

/**
 * Normalizes the persisted `ceInfo` into a `CourseCEInfo` with every
 * field a string (empty when unset). No values are substituted — a field
 * the admin left blank reads back blank, so the CE tab reflects exactly
 * what was authored. Pass the loaded course's `ceInfo` (`course?.ceInfo`);
 * a null/undefined value reads as all-empty.
 */
export function getCEInfo(raw: CourseCeInfo | null | undefined): CourseCEInfo {
  if (!raw) return { ...EMPTY_CE_INFO };
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    agdSubjectCode: str(raw.agdSubjectCode),
    paceProviderId: str(raw.paceProviderId),
    educationalMethod: str(raw.educationalMethod),
    location: str(raw.location),
    accreditationStatement: str(raw.accreditationStatement),
    disclosureStatement: str(raw.disclosureStatement),
    paceBadgeUrl: str(raw.paceBadgeUrl),
  };
}

/** True when at least one CE text field is populated — drives whether
 *  the learner's "CE Information" tab renders content or is hidden. The
 *  badge URL doesn't count: it has a bundled default, so a course with
 *  just the seal and no text is still "empty." */
export function hasCEInfo(raw: CourseCeInfo | null | undefined): boolean {
  if (!raw) return false;
  const textFields: (keyof CourseCeInfo)[] = [
    'agdSubjectCode',
    'paceProviderId',
    'educationalMethod',
    'location',
    'accreditationStatement',
    'disclosureStatement',
  ];
  return textFields.some((k) => {
    const v = raw[k];
    return typeof v === 'string' && v.trim() !== '';
  });
}
